import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as VmsState from '../../backend/index-vms-state';
import { signInWithRedirect, signOut } from '@aws-amplify/auth';
import { azureSsoFlow } from './azureSsoFlow.js';

import { Banner, Button, Spacer, Spinner, TextFieldEditor } from '../../components';
import { LoginForm } from './LoginForm';
import s from './Login.module.scss';

const { NEW_PASSWORDS_MUST_MATCH } = VmsState.constants.LOGIN_ISSUES;
const { BANNER_COLORS, BANNER_ICONS } = VmsState.constants;

class Login extends Component {
    static propTypes = {
        banner: PropTypes.shape({ message: PropTypes.string, color: PropTypes.string, icon: PropTypes.string }),
        dispatch: PropTypes.func,
        loginPending: PropTypes.bool,
        showForgotPassword: PropTypes.bool,
        showForgotPasswordCode: PropTypes.bool,
        showRegisterUser: PropTypes.bool,
        env: PropTypes.string.isRequired,
    };

    static defaultProps = {
        banner: { message: '', color: '', icon: '' },
        dispatch: () => {},
        loginPending: false,
        showForgotPassword: false,
        showForgotPasswordCode: false,
        showRegisterUser: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            code: '',
            email: '',
            firstName: '',
            lastName: '',
            newPassword: '',
            newPasswordConfirmed: '',
            password: '',
            buttonSpinner: ''
        };
    }

    isAcuitySSOEmail = (email) => {
        return /.*@acuitysso\.com$/i.test(email);
    };

    chooseSubtitle = (mustResetPassword, forgotPassword, showForgotCode) => {
        if (mustResetPassword) return 'Please select a new password and complete your registration details';
        if (showForgotCode) return 'Enter the code you received to reset your password';
        if (forgotPassword) return 'Enter your email address below to recieve a password reset code';

        return '';
    };

    handleForgotPasswordEmail = e => {
        e.preventDefault();
        const { dispatch } = this.props;
        const { email } = this.state;

        dispatch(VmsState.SessionReducer.Actions.clearLogin());
        dispatch(VmsState.SessionReducer.Actions.showForgotPasswordCode());
        VmsState.SessionReducer.Thunks.forgotPasswordEmail(email);
    };

    handleForgotPassword = async e => {
        e.preventDefault();
        const { dispatch } = this.props;
        const { code, email, newPassword, newPasswordConfirmed } = this.state;

        if (newPassword !== newPasswordConfirmed) {
            dispatch(VmsState.SessionReducer.Actions.messageBanner(null, BANNER_COLORS.RED, BANNER_ICONS.FAIL, NEW_PASSWORDS_MUST_MATCH));
            return;
        }
        await dispatch(VmsState.SessionReducer.Thunks.forgotPasswordSubmit(email, code, newPassword));
    };

    handleSubmit = async e => {
        e.preventDefault();
        const { email, password } = this.state;
        const { dispatch } = this.props;
        const userData = await dispatch(VmsState.MultiThunks.attemptLogin({ email, password }));
        dispatch(VmsState.ModalReducer.Thunks.setUserDataValues(userData));
    };

    handleCompleteRegisitration = async e => {
        e.preventDefault();
        const { email, password, newPassword, newPasswordConfirmed, firstName, lastName } = this.state;
        const { dispatch } = this.props;

        if (newPassword !== newPasswordConfirmed) {
            dispatch(VmsState.SessionReducer.Actions.messageBanner(null, BANNER_COLORS.RED, BANNER_ICONS.FAIL, NEW_PASSWORDS_MUST_MATCH));
            return;
        }

        await dispatch(VmsState.SessionReducer.Thunks.completeRegistration({ email, password, newPassword, firstName, lastName }));
    };

    handleForgotPasswordClicked = e => {
        e.preventDefault();
        const { dispatch } = this.props;
    
        dispatch(VmsState.SessionReducer.Actions.clearBanner());
        dispatch(VmsState.SessionReducer.Actions.clearLogin());
        dispatch(VmsState.SessionReducer.Actions.showForgotPassword());
    };

    handleReturnToLogin = e => {
        e.preventDefault();
        const { dispatch } = this.props;
        dispatch(VmsState.SessionReducer.Actions.clearBanner());
        dispatch(VmsState.SessionReducer.Actions.clearLogin());
    };

    handleAzureLogin = async () => {
        await azureSsoFlow({
            isLoggedIn: this.props.isLoggedIn,
            signOutFn: () => signOut(),
            signInWithRedirectFn: (payload) => signInWithRedirect(payload)
        });
    };
    
    renderBackToSignIn = () => {
        return (
            <button type="button" className={s.link} onClick={this.handleReturnToLogin}>
                {'< Back to log in'}
            </button>
        );
    };

    renderForgotPasswordEmail = () => {
        const { email } = this.state;
        return (
            <React.Fragment>
                <form onSubmit={this.handleForgotPasswordEmail} data-cy='ForgotPasswordEmailForm'>
                    <TextFieldEditor firstLetterUppercase={true} big label="Email" value={email} onChange={value => this.setState({ email: value })} />
                    <Spacer height={25} />
                    <Button  className={s.primaryBtn} disabled={!email} label="Reset password" />
                </form>
                {this.renderBackToSignIn()}
            </React.Fragment>
        );
    };

    renderForgotPasswordForm = () => {
        const { code, newPassword, newPasswordConfirmed } = this.state;
        const buttonDisabled = !(code && newPassword && newPasswordConfirmed);
        return (
            <React.Fragment>
                <form onSubmit={this.handleForgotPassword}>
                    <TextFieldEditor firstLetterUppercase={true} big label="Code" value={code} onChange={value => this.setState({ code: value })} />
                    <Spacer height={15} />
                    <TextFieldEditor firstLetterUppercase={true} big password label="New Password" value={newPassword} onChange={value => this.setState({ newPassword: value })} />
                    <Spacer height={15} />
                    <TextFieldEditor firstLetterUppercase={true} big password label="Confirm New Password" value={newPasswordConfirmed} onChange={value => this.setState({ newPasswordConfirmed: value })} />
                    <Spacer height={25} />
                    <Button disabled={buttonDisabled} label="Reset password" className={s.primaryBtn} />
                </form>
                {this.renderBackToSignIn()}
            </React.Fragment>
        );
    };

    renderCompleteRegistrationForm = () => {
        const { newPassword, newPasswordConfirmed, firstName, lastName } = this.state;
        return (
            <form onSubmit={this.handleCompleteRegisitration}>
                <TextFieldEditor firstLetterUppercase={true} big label="First name" value={firstName} onChange={value => this.setState({ firstName: value })} />
                <Spacer height={15} />
                <TextFieldEditor firstLetterUppercase={true} big label="Last name" value={lastName} onChange={value => this.setState({ lastName: value })} />
                <Spacer height={15} />
                <TextFieldEditor
                    big
                    password
                    label="Password"
                    value={newPassword}
                    onChange={value => this.setState({ newPassword: value })}
                    firstLetterUppercase={true}
                />
                <Spacer height={15} />
                <TextFieldEditor
                    big
                    password
                    label="Confirm password"
                    value={newPasswordConfirmed}
                    onChange={value => this.setState({ newPasswordConfirmed: value })}
                    firstLetterUppercase={true}
                />
                <Spacer height={25} />
                <Button label={this.renderSpinner('Complete registration')} className={s.primaryBtn} />
            </form>
        );
    };

    renderLoginForm = () => {
            return <LoginForm 
                login={({ email, password, resetSpinner, e}) => {
                    this.setState({ email, password, buttonSpinner: 'LOG IN' }, () => { // Batch the state updates and then invoke submit once state is flushed.
                        this.handleSubmit(e); // TODO: set password/email when typing in fields
                        setTimeout(resetSpinner, 1000);
                    });
                }}
                ssoLogin={this.handleAzureLogin}
                forgotPassword={this.handleForgotPasswordClicked}
             />;
    };

    renderSpinner = label => (this.state.buttonSpinner === label ? <Spinner loading={this.state.buttonSpinner} small /> : label);

    renderForm = (mustResetPassword, forgotPassword, showForgotCode) => {
        if (mustResetPassword) return this.renderCompleteRegistrationForm();
        if (showForgotCode) return this.renderForgotPasswordForm();
        if (forgotPassword) return this.renderForgotPasswordEmail();

        return this.renderLoginForm();
    };

    render() {
        const { banner, showForgotPassword, showForgotPasswordCode, showRegisterUser } = this.props;
        const title = showRegisterUser ? 'Welcome to Wayfinder VMS' : 'Log in to Wayfinder VMS';
        const subtitle = this.chooseSubtitle(showRegisterUser, showForgotPasswordCode, showForgotPasswordCode);

        return (
            <div className={s.login} data-cy='LoginWrapper'>
                <div className={s.splashPanelWrapper}>
                    <div className={s.splashPanel}>
                        <div className={s.locusIcon} />
                        <div className={s.locusLogo}>Wayfinder</div>
                        <div className={s.title}>Venue Management System</div>
                        <div className={s.description}>A powerful, yet easy-to-use application for managing venue geospatial data.</div>
                    </div>
                    <div className={s.version}>Version {import.meta.env.VITE_REACT_APP_VERSION}</div>
                </div>
                <div className={s.formPanel}>
                    <div className={s.loginTitle}>{title}</div>
                    {subtitle && <div className={s.loginSubtitle}>{subtitle}</div>}
                    <Banner banner={banner} className={s.bannerMessage} />

                    {this.renderForm(showRegisterUser, showForgotPassword, showForgotPasswordCode)}
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    featureFlags: state?.featureFlags,
    isLoggedIn: VmsState.SessionReducer.Selectors.isLoggedIn(state),
    loginPending: VmsState.SessionReducer.Selectors.isLoginPending(state),
    banner: VmsState.SessionReducer.Selectors.getMessageBanner(state),
    showForgotPassword: VmsState.SessionReducer.Selectors.getShowForgotPassword(state),
    showForgotPasswordCode: VmsState.SessionReducer.Selectors.getShowForgotPasswordCode(state),
    showRegisterUser: VmsState.SessionReducer.Selectors.getShowRegisterUser(state),
    env: VmsState.selectors.getEnvironment(),
});

export default connect(mapStateToProps)(Login);
